import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
// Wave 0: a guarded delete answers 409 with `detail` naming what still points
// at the row ("3 policies use this RTO"). `error.message` on an axios error is
// "Request failed with status code 409", so using it threw that explanation
// away and left the person with a refusal and no reason. `getErrorMessage`
// reads the server's `detail`.
import { getErrorMessage } from '@/services';
import {
  geographyService,
  CountryFilters,
  StateFilters,
  CityFilters,
  PincodeFilters,
  RTOFilters,
  CountryRequest,
  StateRequest,
  CityRequest,
  PincodeRequest,
  RTORequest,
} from '@/services/geography';

// Query Keys
export const geographyKeys = {
  countries: (page: number, filters?: CountryFilters) => ['countries', page, filters] as const,
  country: (id: string) => ['country', id] as const,
  countryLookup: () => ['countries', 'lookup'] as const,
  states: (page: number, filters?: StateFilters) => ['states', page, filters] as const,
  state: (id: string) => ['state', id] as const,
  stateLookup: (countryId?: string) => ['states', 'lookup', countryId] as const,
  cities: (page: number, filters?: CityFilters) => ['cities', page, filters] as const,
  city: (id: string) => ['city', id] as const,
  cityLookup: (stateId?: string) => ['cities', 'lookup', stateId] as const,
  pincodes: (page: number, filters?: PincodeFilters) => ['pincodes', page, filters] as const,
  pincode: (id: string) => ['pincode', id] as const,
  pincodeSearch: (code: string) => ['pincodes', 'search', code] as const,
  rtos: (page: number, filters?: RTOFilters) => ['rtos', page, filters] as const,
  rto: (id: string) => ['rto', id] as const,
  rtoLookup: (stateId?: string, cityId?: string) => ['rtos', 'lookup', stateId, cityId] as const,
  rtoSearch: (code: string) => ['rtos', 'search', code] as const,
};

// Countries
export function useCountries(page: number = 1, filters?: CountryFilters) {
  return useQuery({
    queryKey: geographyKeys.countries(page, filters),
    queryFn: () => geographyService.getCountries(page, filters),
  });
}

export function useCountry(id: string) {
  return useQuery({
    queryKey: geographyKeys.country(id),
    queryFn: () => geographyService.getCountry(id),
    enabled: !!id,
  });
}

export function useCountryLookup() {
  return useQuery({
    queryKey: geographyKeys.countryLookup(),
    queryFn: () => geographyService.getCountryLookup(),
  });
}

export function useCreateCountry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CountryRequest) => geographyService.createCountry(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['countries'] });
      toast.success('Country created successfully');
    },
    onError: (error: Error) => {
      toast.error(getErrorMessage(error) || 'Failed to create country');
    },
  });
}

export function useUpdateCountry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CountryRequest> }) =>
      geographyService.updateCountry(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['countries'] });
      queryClient.invalidateQueries({ queryKey: geographyKeys.country(id) });
      toast.success('Country updated successfully');
    },
    onError: (error: Error) => {
      toast.error(getErrorMessage(error) || 'Failed to update country');
    },
  });
}

export function useDeleteCountry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => geographyService.deleteCountry(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['countries'] });
      toast.success('Country deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(getErrorMessage(error) || 'Failed to delete country');
    },
  });
}

export function useActivateCountry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => geographyService.activateCountry(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['countries'] });
      queryClient.invalidateQueries({ queryKey: geographyKeys.country(id) });
      toast.success('Country activated');
    },
  });
}

export function usePauseCountry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => geographyService.pauseCountry(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['countries'] });
      queryClient.invalidateQueries({ queryKey: geographyKeys.country(id) });
      toast.success('Country paused');
    },
  });
}

export function useBlockCountry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => geographyService.blockCountry(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['countries'] });
      queryClient.invalidateQueries({ queryKey: geographyKeys.country(id) });
      toast.success('Country blocked');
    },
  });
}

// States
export function useStates(page: number = 1, filters?: StateFilters) {
  return useQuery({
    queryKey: geographyKeys.states(page, filters),
    queryFn: () => geographyService.getStates(page, filters),
  });
}

export function useState(id: string) {
  return useQuery({
    queryKey: geographyKeys.state(id),
    queryFn: () => geographyService.getState(id),
    enabled: !!id,
  });
}

export function useStateLookup(countryId?: string) {
  return useQuery({
    queryKey: geographyKeys.stateLookup(countryId),
    queryFn: () => geographyService.getStateLookup(countryId),
  });
}

export function useCreateState() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: StateRequest) => geographyService.createState(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['states'] });
      queryClient.invalidateQueries({ queryKey: ['countries'] });
      toast.success('State created successfully');
    },
    onError: (error: Error) => {
      toast.error(getErrorMessage(error) || 'Failed to create state');
    },
  });
}

export function useUpdateState() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<StateRequest> }) =>
      geographyService.updateState(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['states'] });
      queryClient.invalidateQueries({ queryKey: geographyKeys.state(id) });
      toast.success('State updated successfully');
    },
    onError: (error: Error) => {
      toast.error(getErrorMessage(error) || 'Failed to update state');
    },
  });
}

export function useDeleteState() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => geographyService.deleteState(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['states'] });
      queryClient.invalidateQueries({ queryKey: ['countries'] });
      toast.success('State deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(getErrorMessage(error) || 'Failed to delete state');
    },
  });
}

export function useToggleStateActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => geographyService.toggleStateActive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['states'] });
      toast.success('State status updated');
    },
  });
}

// Cities
export function useCities(page: number = 1, filters?: CityFilters, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: geographyKeys.cities(page, filters),
    queryFn: () => geographyService.getCities(page, filters),
    // Lets a caller that only wants this ON DEMAND -- the States page's
    // "cities linked to this state" detail modal (item 18) -- skip the fetch
    // entirely while nothing is selected, rather than running an unfiltered
    // `state: undefined` query against every city in the system on every
    // render. `undefined` here means "always enabled", so every existing
    // caller that never passed a third argument is unaffected.
    enabled: options?.enabled,
  });
}

export function useCity(id: string) {
  return useQuery({
    queryKey: geographyKeys.city(id),
    queryFn: () => geographyService.getCity(id),
    enabled: !!id,
  });
}

export function useCityLookup(stateId?: string, opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: geographyKeys.cityLookup(stateId),
    queryFn: () => geographyService.getCityLookup(stateId),
    // B9 (AddressFields): a caller that wants City scoped to a State that has
    // not resolved yet needs to hold the request back entirely -- an
    // unscoped `?state=` (i.e. every city nationwide) is not a fallback
    // anyone asked for, unlike every existing caller here, which always
    // wants the (optionally state-scoped) full list and so leaves this
    // default alone.
    enabled: opts?.enabled ?? true,
  });
}

export function useCreateCity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CityRequest) => geographyService.createCity(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cities'] });
      queryClient.invalidateQueries({ queryKey: ['states'] });
      toast.success('City created successfully');
    },
    onError: (error: Error) => {
      toast.error(getErrorMessage(error) || 'Failed to create city');
    },
  });
}

export function useUpdateCity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CityRequest> }) =>
      geographyService.updateCity(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['cities'] });
      queryClient.invalidateQueries({ queryKey: geographyKeys.city(id) });
      toast.success('City updated successfully');
    },
    onError: (error: Error) => {
      toast.error(getErrorMessage(error) || 'Failed to update city');
    },
  });
}

export function useDeleteCity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => geographyService.deleteCity(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cities'] });
      queryClient.invalidateQueries({ queryKey: ['states'] });
      toast.success('City deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(getErrorMessage(error) || 'Failed to delete city');
    },
  });
}

export function useToggleCityActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => geographyService.toggleCityActive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cities'] });
      toast.success('City status updated');
    },
  });
}

// Pincodes
export function usePincodes(page: number = 1, filters?: PincodeFilters) {
  return useQuery({
    queryKey: geographyKeys.pincodes(page, filters),
    queryFn: () => geographyService.getPincodes(page, filters),
  });
}

export function usePincode(id: string) {
  return useQuery({
    queryKey: geographyKeys.pincode(id),
    queryFn: () => geographyService.getPincode(id),
    enabled: !!id,
  });
}

export function useSearchPincodes(code: string) {
  return useQuery({
    queryKey: geographyKeys.pincodeSearch(code),
    queryFn: () => geographyService.searchPincodes(code),
    enabled: code.length >= 3,
  });
}

/** Options for the Post Office filter dropdown (item 20). See getPostOfficeLookup. */
export function usePostOfficeLookup(filters?: { state?: string; city?: string }) {
  return useQuery({
    queryKey: ['pincodes', 'post-offices', filters?.state ?? '', filters?.city ?? ''],
    queryFn: () => geographyService.getPostOfficeLookup(filters),
  });
}

export function useCreatePincode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: PincodeRequest) => geographyService.createPincode(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pincodes'] });
      queryClient.invalidateQueries({ queryKey: ['cities'] });
      toast.success('Pincode created successfully');
    },
    onError: (error: Error) => {
      toast.error(getErrorMessage(error) || 'Failed to create pincode');
    },
  });
}

export function useUpdatePincode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<PincodeRequest> }) =>
      geographyService.updatePincode(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['pincodes'] });
      queryClient.invalidateQueries({ queryKey: geographyKeys.pincode(id) });
      toast.success('Pincode updated successfully');
    },
    onError: (error: Error) => {
      toast.error(getErrorMessage(error) || 'Failed to update pincode');
    },
  });
}

export function useDeletePincode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => geographyService.deletePincode(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pincodes'] });
      queryClient.invalidateQueries({ queryKey: ['cities'] });
      toast.success('Pincode deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(getErrorMessage(error) || 'Failed to delete pincode');
    },
  });
}

export function useTogglePincodeActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => geographyService.togglePincodeActive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pincodes'] });
      toast.success('Pincode status updated');
    },
  });
}

// RTOs
export function useRTOs(page: number = 1, filters?: RTOFilters) {
  return useQuery({
    queryKey: geographyKeys.rtos(page, filters),
    queryFn: () => geographyService.getRTOs(page, filters),
  });
}

export function useRTO(id: string) {
  return useQuery({
    queryKey: geographyKeys.rto(id),
    queryFn: () => geographyService.getRTO(id),
    enabled: !!id,
  });
}

/**
 * M-04: `cityId` narrows the list to one city's offices. Existing callers pass
 * only a state and are unaffected -- the extra key segment is `undefined` for
 * them, so their cache entry is the same one it has always been.
 *
 * `enabled` lets the Add Policy RTO block hold the request back entirely until
 * a state is chosen: an unscoped lookup is every RTO in the country (1,381 on
 * the live master), which is not a dropdown anybody can use.
 */
export function useRTOLookup(stateId?: string, cityId?: string, opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: geographyKeys.rtoLookup(stateId, cityId),
    queryFn: () => geographyService.getRTOLookup(stateId, cityId),
    enabled: opts?.enabled ?? true,
  });
}

export function useSearchRTOs(code: string) {
  return useQuery({
    queryKey: geographyKeys.rtoSearch(code),
    queryFn: () => geographyService.searchRTOs(code),
    enabled: code.length >= 2,
  });
}

export function useCreateRTO() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: RTORequest) => geographyService.createRTO(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rtos'] });
      queryClient.invalidateQueries({ queryKey: ['states'] });
      queryClient.invalidateQueries({ queryKey: ['cities'] });
      toast.success('RTO created successfully');
    },
    onError: (error: Error) => {
      toast.error(getErrorMessage(error) || 'Failed to create RTO');
    },
  });
}

export function useUpdateRTO() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<RTORequest> }) =>
      geographyService.updateRTO(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['rtos'] });
      queryClient.invalidateQueries({ queryKey: geographyKeys.rto(id) });
      toast.success('RTO updated successfully');
    },
    onError: (error: Error) => {
      toast.error(getErrorMessage(error) || 'Failed to update RTO');
    },
  });
}

export function useDeleteRTO() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => geographyService.deleteRTO(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rtos'] });
      queryClient.invalidateQueries({ queryKey: ['states'] });
      queryClient.invalidateQueries({ queryKey: ['cities'] });
      toast.success('RTO deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(getErrorMessage(error) || 'Failed to delete RTO');
    },
  });
}

export function useToggleRTOActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => geographyService.toggleRTOActive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rtos'] });
      toast.success('RTO status updated');
    },
  });
}
